import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, CheckCircle2, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { ROOMS } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DateField = ({ label, value, onChange, disabledBefore, extraDisabled, testId }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0B3B39]">{label}</Label>
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="flex items-center justify-between w-full bg-white border border-[#E6E2D8] rounded-sm px-4 py-3 text-sm text-left hover:border-[#0B3B39] focus:outline-none focus:ring-2 focus:ring-[#0B3B39] transition-colors duration-300"
        >
          <span className={value ? "text-[#1A1A1A]" : "text-[#4A5568]"}>
            {value ? format(value, "EEE, dd MMM yyyy") : "Select date"}
          </span>
          <CalendarIcon className="w-4 h-4 text-[#0B3B39]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          disabled={(d) =>
            (disabledBefore ? d < disabledBefore : d < new Date(new Date().setHours(0, 0, 0, 0))) ||
            (extraDisabled ? extraDisabled(d) : false)
          }
          initialFocus
        />
      </PopoverContent>
    </Popover>
  </div>
);

export const ReservationForm = ({ selectedRoom }) => {
  const [form, setForm] = useState({
    guest_name: "",
    email: "",
    phone: "",
    guests: "",
    notes: "",
  });
  const [items, setItems] = useState([{ room_type: "", rooms: "1" }]);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [booked, setBooked] = useState([]);
  const [inventory, setInventory] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const toDay = (iso) => new Date(`${iso}T00:00:00`).getTime();

  useEffect(() => {
    axios
      .get(`${API}/availability`)
      .then(({ data }) => {
        setBooked(data.booked || []);
        setInventory(data.inventory || {});
      })
      .catch(() => {});
  }, []);

  const chosenTypes = items.map((i) => i.room_type).filter(Boolean);

  // occupancy of a room type on a single night (in units — a booking may take several rooms)
  const occupancy = (type, nightTs) =>
    booked
      .filter((b) => b.room_type === type && toDay(b.check_in) <= nightTs && nightTs < toDay(b.check_out))
      .reduce((sum, b) => sum + (b.rooms || 1), 0);

  // a night is unbookable only when EVERY chosen type is sold out that night
  const allSoldOut = (nightTs) =>
    chosenTypes.length > 0 && chosenTypes.every((t) => occupancy(t, nightTs) >= (inventory[t] || 1));

  const isBookedDay = (d) => allSoldOut(d.getTime());

  // a check-out day is blocked if any night in the stay is impossible for all chosen types
  const isBlockedCheckout = (d) => {
    if (!checkIn) return false;
    for (let t = checkIn.getTime(); t < d.getTime(); t += 86400000) {
      if (allSoldOut(t)) return true;
    }
    return false;
  };

  // units remaining for a type across the selected stay
  const remainingFor = (type) => {
    if (!checkIn || !checkOut || !type) return null;
    let maxOcc = 0;
    for (let t = checkIn.getTime(); t < checkOut.getTime(); t += 86400000) {
      maxOcc = Math.max(maxOcc, occupancy(type, t));
    }
    return (inventory[type] || 1) - maxOcc;
  };

  const datesChosen = Boolean(checkIn && checkOut);
  const shortfalls = datesChosen
    ? items.filter((i) => i.room_type && Number(i.rooms) > Math.max(remainingFor(i.room_type) ?? 0, 0))
    : [];

  useEffect(() => {
    if (selectedRoom) {
      setItems([{ room_type: selectedRoom, rooms: "1" }]);
      document.getElementById("reserve")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedRoom]);

  const setItem = (idx, patch) => setItems((its) => its.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkIn || !checkOut) {
      toast.error("Please choose your check-in and check-out dates.");
      return;
    }
    if (checkOut <= checkIn) {
      toast.error("Check-out must be after check-in.");
      return;
    }
    if (items.some((i) => !i.room_type)) {
      toast.error("Please choose a room type for each room row.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/reservations`, {
        guest_name: form.guest_name,
        email: form.email,
        phone: form.phone,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        items: items.map((i) => ({ room_type: i.room_type, rooms: Number(i.rooms) })),
        guests: Number(form.guests),
        notes: form.notes,
      });
      setConfirmation(data);
      toast.success("Reservation request sent! We will confirm shortly.");
      setForm({ guest_name: "", email: "", phone: "", guests: "", notes: "" });
      setItems([{ room_type: "", rooms: "1" }]);
      setCheckIn(null);
      setCheckOut(null);
      axios.get(`${API}/availability`).then(({ data: av }) => setBooked(av.booked || [])).catch(() => {});
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not send your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reserve" data-testid="reservation-section" className="py-24 md:py-32 bg-[#F3F0EA]">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C5A880]">Reservations</p>
          <h2 className="font-serif-display font-light text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#0B3B39] mt-4 leading-tight">
            Reserve your stay.
          </h2>
          <p className="text-base leading-relaxed text-[#4A5568] mt-6 max-w-xl mx-auto">
            Pick your dates and room — your request goes straight to our team, and we confirm availability personally.
          </p>
        </div>

        {confirmation ? (
          <div
            data-testid="reservation-confirmation"
            className="mt-14 bg-white border border-[#E6E2D8] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10 text-center"
          >
            <CheckCircle2 className="w-12 h-12 text-[#0B3B39] mx-auto" strokeWidth={1.5} />
            <h3 className="font-serif-display text-2xl text-[#0B3B39] mt-5">Request received, {confirmation.guest_name.split(" ")[0]}.</h3>
            <p className="text-sm leading-relaxed text-[#4A5568] mt-3">
              {confirmation.items?.map((it) => `${it.rooms} × ${it.room_type}`).join("  +  ")} ·{" "}
              {format(new Date(confirmation.check_in), "dd MMM yyyy")} →{" "}
              {format(new Date(confirmation.check_out), "dd MMM yyyy")} · {confirmation.nights}{" "}
              {confirmation.nights === 1 ? "night" : "nights"} · {confirmation.guests}{" "}
              {confirmation.guests === 1 ? "guest" : "guests"}
            </p>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C5A880] mt-4">
              Reference: {confirmation.id.slice(0, 8).toUpperCase()}
            </p>
            <p className="text-sm text-[#4A5568] mt-4">
              We will reach you at {confirmation.email} or {confirmation.phone} to confirm.
            </p>
            <button
              data-testid="make-another-reservation-btn"
              onClick={() => setConfirmation(null)}
              className="mt-8 border border-[#0B3B39] text-[#0B3B39] text-xs uppercase tracking-[0.2em] font-semibold px-8 py-3 rounded-sm hover:bg-[#0B3B39] hover:text-[#FDFBF7] transition-colors duration-300"
            >
              Make Another Request
            </button>
          </div>
        ) : (
          <form
            data-testid="reservation-form"
            onSubmit={handleSubmit}
            className="mt-14 bg-white border border-[#E6E2D8] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DateField label="Check-in" value={checkIn} onChange={(d) => { setCheckIn(d); if (checkOut && d && checkOut <= d) setCheckOut(null); }} extraDisabled={isBookedDay} testId="check-in-input" />
              <DateField
                label="Check-out"
                value={checkOut}
                onChange={setCheckOut}
                disabledBefore={checkIn ? new Date(checkIn.getTime() + 86400000) : undefined}
                extraDisabled={isBlockedCheckout}
                testId="check-out-input"
              />

              <div className="flex flex-col gap-4 md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0B3B39]">Rooms</Label>
                {items.map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-end" data-testid={`room-row-${idx}`}>
                    <div className="flex-1">
                      <Select value={item.room_type} onValueChange={(v) => setItem(idx, { room_type: v, rooms: "1" })}>
                        <SelectTrigger data-testid={`room-type-select-${idx}`} className="bg-white border-[#E6E2D8] rounded-sm px-4 py-3 h-auto text-sm">
                          <SelectValue placeholder="Choose a room type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {ROOMS.filter((r) => r.name === item.room_type || !chosenTypes.includes(r.name)).map((r) => (
                            <SelectItem key={r.id} value={r.name} data-testid={`room-option-${r.id}`}>
                              {r.name} — {r.price}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-28">
                      <Select value={item.rooms} onValueChange={(v) => setItem(idx, { rooms: v })} disabled={!item.room_type}>
                        <SelectTrigger data-testid={`rooms-select-${idx}`} className="bg-white border-[#E6E2D8] rounded-sm px-4 py-3 h-auto text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {Array.from({ length: inventory[item.room_type] || 1 }, (_, i) => String(i + 1)).map((n) => (
                            <SelectItem key={n} value={n} data-testid={`rooms-option-${n}`}>
                              {n}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {items.length > 1 && (
                      <button
                        type="button"
                        data-testid={`remove-room-row-${idx}`}
                        onClick={() => setItems(items.filter((_, i) => i !== idx))}
                        aria-label="Remove room"
                        className="p-3 border border-[#E6E2D8] text-[#4A5568] rounded-sm hover:border-red-300 hover:text-red-700 transition-colors duration-300"
                      >
                        <X size={15} />
                      </button>
                    )}
                  </div>
                ))}
                {items.length < ROOMS.length && !items.some((i) => !i.room_type) && (
                  <button
                    type="button"
                    data-testid="add-room-type-btn"
                    onClick={() => setItems([...items, { room_type: "", rooms: "1" }])}
                    className="self-start text-xs uppercase tracking-[0.2em] font-semibold text-[#0B3B39] border-b border-[#C5A880] pb-1 hover:text-[#C5A880] transition-colors duration-300"
                  >
                    + Add another room type
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0B3B39]">Guests</Label>
                <Select value={form.guests} onValueChange={(v) => setForm((f) => ({ ...f, guests: v }))} required>
                  <SelectTrigger data-testid="guests-select" className="bg-white border-[#E6E2D8] rounded-sm px-4 py-3 h-auto text-sm">
                    <SelectValue placeholder="How many?" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {["1", "2", "3", "4", "5", "6", "7", "8+"].map((n) => (
                      <SelectItem key={n} value={n === "8+" ? "8" : n} data-testid={`guests-option-${n}`}>
                        {n} {n === "1" ? "guest" : "guests"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="guest-name" className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0B3B39]">Full Name</Label>
                <Input id="guest-name" data-testid="guest-name-input" required minLength={2} value={form.guest_name} onChange={set("guest_name")} placeholder="Your name" className="bg-white border-[#E6E2D8] rounded-sm px-4 py-3 h-auto text-sm" />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="guest-phone" className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0B3B39]">Phone</Label>
                <Input id="guest-phone" data-testid="guest-phone-input" required minLength={6} value={form.phone} onChange={set("phone")} placeholder="+1 ..." className="bg-white border-[#E6E2D8] rounded-sm px-4 py-3 h-auto text-sm" />
              </div>

              <div className="flex flex-col gap-2 md:col-span-2">
                <Label htmlFor="guest-email" className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0B3B39]">Email</Label>
                <Input id="guest-email" data-testid="guest-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className="bg-white border-[#E6E2D8] rounded-sm px-4 py-3 h-auto text-sm" />
              </div>

              <div className="flex flex-col gap-2 md:col-span-2">
                <Label htmlFor="guest-notes" className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0B3B39]">Special Requests <span className="normal-case tracking-normal text-[#4A5568] font-normal">(optional)</span></Label>
                <Textarea id="guest-notes" data-testid="guest-notes-input" value={form.notes} onChange={set("notes")} placeholder="Anniversary, dietary needs, early check-in..." className="bg-white border-[#E6E2D8] rounded-sm px-4 py-3 text-sm min-h-[100px]" />
              </div>
            </div>

            {datesChosen && chosenTypes.length > 0 && (
              <div
                data-testid="availability-status"
                className={`mt-6 text-sm font-semibold text-center px-4 py-3 border ${
                  shortfalls.length
                    ? "text-red-700 bg-red-50 border-red-200"
                    : "text-[#0B3B39] bg-emerald-50 border-emerald-200"
                }`}
              >
                {shortfalls.length
                  ? shortfalls
                      .map((i) => `Only ${Math.max(remainingFor(i.room_type) ?? 0, 0)} × ${i.room_type} left for your dates`)
                      .join(" · ") + " — adjust rooms or dates"
                  : items
                      .filter((i) => i.room_type)
                      .map((i) => `${i.room_type}: ${remainingFor(i.room_type)} of ${inventory[i.room_type] || 1} available`)
                      .join("  ·  ")}
              </div>
            )}

            <button
              type="submit"
              data-testid="reservation-submit-btn"
              disabled={submitting || shortfalls.length > 0}
              className="mt-8 w-full bg-[#0B3B39] text-[#FDFBF7] text-xs uppercase tracking-[0.25em] font-bold px-8 py-4 rounded-sm hover:bg-[#072A28] disabled:opacity-60 transition-colors duration-300"
            >
              {submitting ? "Sending..." : "Send Reservation Request"}
            </button>
            <p className="text-xs text-[#4A5568] text-center mt-4">
              No payment now — we confirm availability and details with you directly.
            </p>
          </form>
        )}
      </div>
    </section>
  );
};
